import type { ReactElement } from 'react';
import { User, ExternalLink, Mail, Phone, Building, ChevronRight } from 'lucide-react';
import { useTranslation } from '@govard/i18n-core/react';
import type { StructuredMessagePayload } from '@govard/ai-chat-kit';

interface ContactSnippetProps {
  contact: Extract<StructuredMessagePayload, { type: 'contact' }>['contact'];
  onOpenEntity?:
    | ((type: 'deal' | 'contact', id: string, title?: string | undefined) => void)
    | undefined;
  compact?: boolean | undefined;
  className?: string | undefined;
}

export function ContactSnippet({
  contact,
  onOpenEntity,
  compact = false,
  className = '',
}: ContactSnippetProps): ReactElement {
  const { t } = useTranslation();
  const handleClick = () => {
    onOpenEntity?.('contact', contact.id, contact.name);
  };

  // ─── LobeHub High-Density Row View ───────────────────────────────────────────
  if (compact) {
    return (
      <div
        data-testid="structured-snippet-contact"
        onClick={handleClick}
        className={`group flex items-center justify-between gap-3 px-3.5 py-2 text-xs transition-colors hover:bg-zinc-50/80 cursor-pointer ${className}`}
      >
        <div className="flex min-w-0 items-center gap-2.5 flex-1">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-600">
            <User size={13} />
          </div>
          <span className="font-medium text-zinc-900 group-hover:text-zinc-950 truncate">
            {contact.name}
          </span>
          {contact.company && (
            <span className="text-[11px] text-zinc-400 shrink-0">• {contact.company}</span>
          )}
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          {contact.email && (
            <span className="hidden sm:inline-flex text-[11px] text-zinc-500 truncate max-w-[150px]">
              {contact.email}
            </span>
          )}

          {contact.phone && (
            <span className="text-[11px] font-mono text-zinc-600">{contact.phone}</span>
          )}

          <ChevronRight
            size={13}
            className="text-zinc-400 group-hover:text-zinc-700 transition-transform group-hover:translate-x-0.5"
          />
        </div>
      </div>
    );
  }

  // ─── Standalone Detailed Card View ───────────────────────────────────────────
  return (
    <div
      data-testid="structured-snippet-contact"
      onClick={handleClick}
      className={`group my-1.5 flex flex-col gap-2 rounded-xl border border-zinc-200/90 bg-white p-3.5 text-xs shadow-2xs transition-all hover:border-zinc-300 cursor-pointer ${className}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700">
            <User size={14} />
          </div>
          <span className="truncate font-semibold text-zinc-900">{contact.name}</span>
        </div>

        {contact.company && (
          <span className="flex items-center gap-1 rounded border border-zinc-200/70 bg-zinc-50 px-2 py-0.5 text-[10px] font-medium text-zinc-600">
            <Building size={10} className="text-zinc-400" />
            <span className="truncate max-w-[120px]">{contact.company}</span>
          </span>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-zinc-100 pt-2 text-[11px] text-zinc-500">
        <div className="flex items-center gap-3">
          {contact.email && (
            <span className="flex items-center gap-1 text-zinc-600">
              <Mail size={11} className="text-zinc-400" />
              <span className="truncate max-w-[150px]">{contact.email}</span>
            </span>
          )}
          {contact.phone && (
            <span className="flex items-center gap-1 text-zinc-600 font-mono">
              <Phone size={11} className="text-zinc-400" />
              <span>{contact.phone}</span>
            </span>
          )}
        </div>

        <span className="inline-flex items-center gap-1 font-medium text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity">
          <span>{t('crm.snippets.open')}</span>
          <ExternalLink size={11} />
        </span>
      </div>
    </div>
  );
}
